import { useLocation } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { Modal } from '@/shared/components/Modal'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSidebar } from '@/context/SidebarContext'
import { api } from '@/lib/api'
import { trackEvent } from '@/lib/telemetry'
import { styles } from '@/shell/layout.styles'
import { NAV_ICONS } from '@/shell/nav/navIcons'

export function PlansIntroModal({ onClose }: { onClose: () => void }) {
  // Same self-contained "coming soon" pattern as DriftDetectionModal/AuditModal:
  // interest fires on the explicit "Register interest" click (never on open), and
  // feedback is inline because a toast can't paint over a native <dialog>.
  const code = { fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--accent)' } as const
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])

  async function register() {
    setState('submitting')
    setErrorMsg('')
    try {
      trackEvent('register_interest', 'plans')
      await api.post('/api/interest/plans')
      if (mountedRef.current) setState('success')
    } catch (err) {
      if (!mountedRef.current) return
      setState('error')
      setErrorMsg(err instanceof Error ? err.message : 'Network error. Please try again.')
    }
  }

  return (
    <Modal track="plans_intro" title="Plans" kicker="Coming soon" onClose={onClose} maxWidth={720}>
      <p className="guide-para">
        Plans will centralise every <code style={code}>terraform plan</code> / <code style={code}>tofu plan</code>{' '}
        your pipelines submit, so it's authorised against policy before anyone runs{' '}
        <code style={code}>apply</code> - not after.
      </p>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 6 }}>
        How it would work
      </div>
      <ul className="guide-list">
        <li><strong>Policy gate before apply</strong> - OPA/Rego policies run against each submitted plan; failures are reported and can block the apply.</li>
        <li><strong>Staleness checked against state</strong> - every plan is compared against the workspace's current state serial, so one made against stale state is flagged before it's approved.</li>
        <li><strong>History, not a one-off check</strong> - every plan version is kept with its exact adds/changes/destroys/replaces, so a destructive plan from last week stays answerable.</li>
        <li><strong>Provider-aware replacements</strong> - replacements break down by the provider driving them, so a noisy provider bump is obvious instead of lost in a flat resource list.</li>
      </ul>
      <div className="callout-accent">
        <strong>Not ready yet.</strong> Register your interest so we can prioritise it.
      </div>
      {state === 'success' ? (
        <div style={{ marginTop: 16 }}>
          <div className="feedback-success">Thanks - we'll prioritise it.</div>
          <div className="modal-actions">
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      ) : (
        <>
          {state === 'error' && (
            <div className="feedback-error" style={{ marginTop: 10 }}>{errorMsg}</div>
          )}
          <div className="modal-actions">
            <button className="btn btn-sm" onClick={onClose}>Close</button>
            <button className="btn btn-primary btn-sm" onClick={register} disabled={state === 'submitting'}>
              {state === 'submitting' ? 'Registering…' : 'Register interest'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

export function PlansNavButton({ orgBase }: { orgBase: string }) {
  const { collapsed } = useSidebar()
  const { pathname } = useLocation()
  const [hovered, setHovered] = useState(false)
  const [show, setShow] = useState(false)

  // The modal no longer navigates, but the /plans route still exists, so keep the
  // active style honest for anyone who deep-links straight to it.
  const active = pathname.startsWith(`${orgBase}/plans`)

  return (
    <>
      {show && <PlansIntroModal onClose={() => setShow(false)} />}
      <Tooltip text="Centrally authorise Terraform plans, then report and alert on policy failures" placement="right">
        <button
          style={{ ...styles.navItem, ...(collapsed ? styles.navItemCollapsed : {}), ...(active ? styles.navItemActive : hovered ? styles.navItemHover : {}), width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
          onClick={() => setShow(true)}
          aria-label={collapsed ? 'Plans' : undefined}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {NAV_ICONS.plans}
          {!collapsed && 'Plans'}
        </button>
      </Tooltip>
    </>
  )
}

